import { stringify } from 'query-string';
import { fetchUtils, DataProvider } from 'react-admin';
import { queryString, toCamelCase } from '../common/UtilFunctions';

/**
 * Maps react-admin queries to a json-server powered REST API
 *
 * @see https://github.com/typicode/json-server
 *
 * @example
 *
 * getList          => GET http://my.api.url/posts?_sort=title&_order=ASC&_start=0&_end=24
 * getOne           => GET http://my.api.url/posts/123
 * getManyReference => GET http://my.api.url/posts?author_id=345
 * getMany          => GET http://my.api.url/posts?id=123&id=456&id=789
 * create           => POST http://my.api.url/posts/123
 * update           => PUT http://my.api.url/posts/123
 * updateMany       => PUT http://my.api.url/posts/123, PUT http://my.api.url/posts/456, PUT http://my.api.url/posts/789
 * delete           => DELETE http://my.api.url/posts/123
 *
 * @example
 *
 * import * as React from "react";
 * import { Admin, Resource } from 'react-admin';
 * import jsonServerProvider from 'ra-data-json-server';
 *
 * import { PostList } from './posts';
 *
 * const App = () => (
 *     <Admin dataProvider={jsonServerProvider('http://jsonplaceholder.typicode.com')}>
 *         <Resource name="posts" list={PostList} />
 *     </Admin>
 * );
 *
 * export default App;
 */
export default (apiUrl, httpClient = fetchUtils.fetchJson): DataProvider => ({
    getList: (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const { search, ...filters } = params.filter;
        
        const query = {
            filters: fetchUtils.flattenObject(filters),
            search: search ?? null,
            sortBy: field,
            sortDirection: order,
            page: page,
            limit: perPage,
        };

        const url = `${apiUrl}/${resource}?${queryString(query)}`;

        return httpClient(url).then(({ status, headers, json }) => {
           if(json.results !== undefined){  
                return {
                    data: json.results,
                    total: json.pagination.total_count
                };
            }
            else
            {
                let result =  json.map((item, index) => {
                    return {
                        ...item,
                        id: index
                    }
                })
                return {
                    data: result,
                    total: result.length
                }
            }
        });
    },

    get: (resource, query) => {
        const url = `${apiUrl}/${resource}?${queryString(query)}`;

        return httpClient(url).then(({json}) => ({
            data: json,
        }))
    },

    getOne: (resource, params) => {
        return httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
            data: json,
        }))
    },

    getMany: (resource, params) => {
        const query = {
            filters: {"id.in": params.ids.toString()},
        };

        const url = `${apiUrl}/${resource}?${queryString(query)}`;

        return httpClient(url).then(({ json }) => { 
            return {
                data: json.results
            };
        });
    },

    getManyReference: (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
            ...fetchUtils.flattenObject(params.filter),
            [params.target]: params.id,
            _sort: field,
            _order: order,
            _start: (page - 1) * perPage,
            _end: page * perPage,
        };
        const url = `${apiUrl}/${resource}?${stringify(query)}`;

        return httpClient(url).then(({ headers, json }) => {
            if (!headers.has('x-total-count')) {
                throw new Error(
                    'The X-Total-Count header is missing in the HTTP Response. The jsonServer Data Provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare X-Total-Count in the Access-Control-Expose-Headers header?'
                );
            }
            return {
                data: json,
                total: parseInt(
                    headers.get('x-total-count').split('/').pop(),
                    10
                ),
            };
        });
    },

    update: (resource, params) => {
        let id = params.id ? `/${params.id}` : "";
        const method = params?.data?.method || 'PUT';
        if (params?.data?.method) {
            delete params.data.method;
        }
        
        return httpClient(`${apiUrl}/${resource}` + id, {
            method: method,
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json }));


    },

    patch: (resource, params) => {
        let id = params.id ? `/${params.id}` : "";
        return httpClient(`${apiUrl}/${resource}` + id, {
            method: 'PATCH',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json }));
    },

    updateMany: (resource, params) =>
        Promise.all(
            params.ids.map(id =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(params.data),
                })
            )
        ).then(responses => ({ data: responses.map(({ json }) => json.id) })),

    create: (resource, params) =>
        httpClient(`${apiUrl}/${resource}`, {
            method: 'POST',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({
            data: { ...params.data, id: json.id },
        })),

    delete: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'DELETE',
        }).then(({ json }) => ({ data: json })),

    deleteMany: (resource, params) =>
        Promise.all(
            params.ids.map(id =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'DELETE',
                })
            )
        ).then(responses => ({ data: responses.map(({ json }) => json.id) })),
});
